// @contexts/DocumentAssistantSupplyClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from "react";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";
import { requireContext } from "@contexts/requireContext";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import {
    DefaultDocumentAssistantSupplyClient,
    type DocumentAssistantSupplyClient,
} from "@services/clients/DocumentAssistantSupplyClient";
import { StubDocumentAssistantSupplyClient } from "@services/stubs/StubDocumentAssistantSupplyClient";

const defaultStubDocumentAssistantSupplyClient = new StubDocumentAssistantSupplyClient();

const DocumentAssistantSupplyClientContext = createContext<OptionTs<DocumentAssistantSupplyClient>>(
    Option.none<DocumentAssistantSupplyClient>(),
);

type DocumentAssistantSupplyClientProviderProps = {
    readonly children: ReactNode;
} & (
    | {
          readonly mode: "default";
      }
    | {
          readonly mode: "defaultStub";
      }
    | {
          readonly client: DocumentAssistantSupplyClient;
      }
);

export const DocumentAssistantSupplyClientProvider: React.FC<DocumentAssistantSupplyClientProviderProps> = (props) => {
    const client = useMemo<DocumentAssistantSupplyClient>(() => {
        if ("client" in props) {
            return props.client;
        }
        if (props.mode === "defaultStub" || enabledFeatures[FeaturesEnum.StubClients]) {
            return defaultStubDocumentAssistantSupplyClient;
        }
        return createDefaultDocumentAssistantSupplyClient();
    }, [props]);

    const contextValue = useMemo(
        () => Option.some(client),
        [client],
    );

    return (
        <DocumentAssistantSupplyClientContext.Provider value={contextValue}>
            {props.children}
        </DocumentAssistantSupplyClientContext.Provider>
    );
};

export const createDefaultDocumentAssistantSupplyClient = (): DocumentAssistantSupplyClient =>
    new DefaultDocumentAssistantSupplyClient();

export const useDocumentAssistantSupplyClient = (): DocumentAssistantSupplyClient => {
    return requireContext(
        useContext(DocumentAssistantSupplyClientContext),
        "useDocumentAssistantSupplyClient must be used within a DocumentAssistantSupplyClientProvider",
    );
};
